import { useCallback, useEffect, useState } from 'react';
import { leave } from '../api/client';
import { isoDate } from '../util/dates';


export default function MyLeave({ onError, onNotice }) {
  const [balances, setBalances] = useState([]);
  const [requests, setRequests] = useState([]);
  const [types, setTypes] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [form, setForm] = useState({
    leaveTypeId: '', fromDate: isoDate(), toDate: isoDate(),
    halfDay: false, reason: '', contactDuringLeave: '',
  });
  const [cost, setCost] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [b, r, t, h] = await Promise.all([
        leave.balances(), leave.requests(), leave.types(), leave.holidays(),
      ]);
      setBalances(b || []);
      setRequests(r || []);
      setTypes(t || []);
      setHolidays(h || []);
      setForm((f) => ({ ...f, leaveTypeId: f.leaveTypeId || (t && t[0] ? t[0].id : '') }));
    } catch (e) {
      onError(e.message);
    }
  }, [onError]);

  useEffect(() => { load(); }, [load]);

  // Show the real cost before submitting - weekends and holidays are excluded
  // server-side, so a 5-day span is often not 5 days of leave.
  useEffect(() => {
    if (!form.fromDate || !form.toDate) return;
    if (form.halfDay) { setCost(0.5); return; }
    let live = true;
    leave.workingDays(form.fromDate, form.toDate)
      .then((d) => live && setCost(d))
      .catch(() => live && setCost(null));
    return () => { live = false; };
  }, [form.fromDate, form.toDate, form.halfDay]);

  const set = (k) => (e) =>
    setForm({ ...form, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });

  async function apply(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await leave.apply({ ...form, leaveTypeId: Number(form.leaveTypeId) });
      onNotice('Leave request submitted');
      setForm({ ...form, reason: '', contactDuringLeave: '' });
      await load();
    } catch (err) {
      onError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function cancel(id) {
    try {
      await leave.cancel(id);
      onNotice('Request withdrawn');
      await load();
    } catch (err) {
      onError(err.message);
    }
  }

  return (
    <div>
      <h2>My leave</h2>
      <p className="muted">Balances accrue monthly. Weekends and company holidays are not counted.</p>

      <div className="strip" style={{ marginTop: 14 }}>
        {balances.map((b) => (
          <div className="strip-cell balance-cell" key={b.leaveTypeId}>
            <div className="strip-value">{b.available}</div>
            <div className="strip-label">{b.code} available</div>
            <div className="small muted">
              {b.accrued} accrued{b.carriedForward > 0 && ` · ${b.carriedForward} c/f`}
              {b.adjustment !== 0 && b.adjustment !== undefined && ` · ${b.adjustment > 0 ? '+' : ''}${b.adjustment} set by HR`}
              {b.used > 0 && ` · ${b.used} used`}
              {b.pending > 0 && ` · ${b.pending} pending`}
            </div>
          </div>
        ))}
      </div>

      <form className="card form-grid" onSubmit={apply}>
        <h3 className="span-all">Request leave</h3>
        <label className="field">
          <span>Type</span>
          <select value={form.leaveTypeId} onChange={set('leaveTypeId')} required>
            {types.map((t) => <option key={t.id} value={t.id}>{t.code} · {t.name}</option>)}
          </select>
        </label>
        <label className="field">
          <span>From</span>
          <input type="date" value={form.fromDate} onChange={set('fromDate')} required />
        </label>
        <label className="field">
          <span>To</span>
          <input type="date" value={form.toDate} onChange={set('toDate')}
                 disabled={form.halfDay} required />
        </label>
        <label className="field">
          <span>Contact while away</span>
          <input value={form.contactDuringLeave} onChange={set('contactDuringLeave')} placeholder="Phone" />
        </label>
        <label className="check span-all">
          <input type="checkbox" checked={form.halfDay}
                 onChange={(e) => setForm({
                   ...form, halfDay: e.target.checked,
                   toDate: e.target.checked ? form.fromDate : form.toDate,
                 })} />
          Half day
        </label>
        <label className="field span-all">
          <span>Reason</span>
          <textarea rows={2} value={form.reason} onChange={set('reason')} />
        </label>
        <div className="span-all actions">
          <button className="btn btn-primary" disabled={busy}>Submit request</button>
          {cost !== null && (
            <span className="muted small">
              This will use <strong>{cost}</strong> day(s) of leave
            </span>
          )}
        </div>
      </form>

      <h3>My requests</h3>
      {requests.length === 0 ? (
        <div className="card empty"><p className="muted">No leave requests yet.</p></div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr><th>Dates</th><th>Days</th><th>Reason</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              {requests.map((r) => (
                <tr key={r.id}>
                  <td className="nowrap">
                    {r.fromDate}{r.toDate !== r.fromDate && ` → ${r.toDate}`}
                    {r.halfDay && <span className="small muted"> (half)</span>}
                  </td>
                  <td>{r.days}</td>
                  <td className="small">{r.reason || '—'}
                    {r.decisionNote && <div className="small muted">↳ {r.decisionNote}</div>}
                  </td>
                  <td><span className={`pill pill-${String(r.status).toLowerCase()}`}>{r.status}</span></td>
                  <td>
                    {r.status === 'SUBMITTED' && (
                      <button className="btn btn-sm btn-danger" onClick={() => cancel(r.id)}>Withdraw</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {holidays.length > 0 && (
        <div className="card" style={{ marginTop: 16 }}>
          <h3>Company holidays</h3>
          <div className="holiday-list">
            {holidays.map((h) => (
              <div key={h.id} className="holiday">
                <strong>{h.holidayDate}</strong> <span className="muted">{h.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
